import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Closure, ClosureField } from '@smile/shared';
import { CLOSURE_FIELDS } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { todayISO } from '../lib/filters';

/**
 * Nastavení → Uzavírky. The legacy page listed the closed days ahead with
 * four ticks each — příjezdy / odjezdy at Tuchoměřice, and the same at
 * Europort — and a form to add a day. A tick toggles at once, the way the
 * old `?date=&direction=&set=` links did; the day drops off the list when
 * its last gate opens again. The web site refuses a booking on a closed
 * day; the desk sees a warning under the price and books anyway.
 */
const GATE_LABEL: Record<ClosureField, string> = {
  arrival: 'Příjezdy',
  departure: 'Odjezdy',
  arrivalEuroport: 'Příjezdy Europort',
  departureEuroport: 'Odjezdy Europort',
};

function czDay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}. ${y}`;
}

function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}

export function UzavirkyCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['settings', 'closures'], queryFn: () => api.closures() });
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');
  const [gates, setGates] = useState<Record<ClosureField, boolean>>({
    arrival: true,
    departure: false,
    arrivalEuroport: false,
    departureEuroport: false,
  });

  const toggle = useMutation({
    mutationFn: (v: { day: string; field: ClosureField; closed: boolean }) => api.setClosure(v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'closures'] }),
  });
  const add = useMutation({
    mutationFn: async () => {
      const picked = CLOSURE_FIELDS.filter((f) => gates[f]);
      let first = true;
      for (const field of picked) {
        await api.setClosure({ day, field, closed: true, ...(first ? { note: note.trim() } : {}) });
        first = false;
      }
    },
    onSuccess: async () => {
      setDay('');
      setNote('');
      await qc.invalidateQueries({ queryKey: ['settings', 'closures'] });
    },
  });
  const anyGate = CLOSURE_FIELDS.some((f) => gates[f]);
  const dayOk = /^\d{4}-\d{2}-\d{2}$/.test(day);
  const message = errText(toggle.error) ?? errText(add.error);

  return (
    <div className="card detailcard">
      <h2>Uzavírky</h2>
      <p className="settings-note">
        Dny, kdy je brána zavřená: web takový termín nepřijme, přepážka dostane u ceny varování. Zaškrtnutí platí hned;
        když se otevře poslední brána, den ze seznamu zmizí.
      </p>
      {q.isPending ? (
        <div className="empty">Načítám…</div>
      ) : q.isError ? (
        <div className="empty" role="alert">Uzavírky se nepodařilo načíst.</div>
      ) : (q.data as Closure[]).length === 0 ? (
        <div className="empty">Žádná uzavírka od dneška dál.</div>
      ) : (
        <table className="mini settings closures">
          <thead>
            <tr>
              <th>Den</th>
              {CLOSURE_FIELDS.map((f) => (
                <th key={f} className="num">
                  {GATE_LABEL[f]}
                </th>
              ))}
              <th>Poznámka</th>
              <th>Nastavil</th>
            </tr>
          </thead>
          <tbody>
            {(q.data as Closure[]).map((c) => (
              <tr key={c.day}>
                <td>
                  <b>{czDay(c.day)}</b>
                </td>
                {CLOSURE_FIELDS.map((f) => (
                  <td key={f} className="num">
                    <input
                      type="checkbox"
                      checked={c[f]}
                      disabled={!canWrite || toggle.isPending}
                      onChange={(e) => toggle.mutate({ day: c.day, field: f, closed: e.target.checked })}
                      aria-label={`${czDay(c.day)} — ${GATE_LABEL[f]}`}
                    />
                  </td>
                ))}
                <td>{c.note || <span className="was">—</span>}</td>
                <td className="was">{c.setBy ? `${c.setBy} · ${c.setAt.slice(0, 16)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canWrite ? (
        <form
          className="editform closure-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (dayOk && anyGate && !add.isPending) add.mutate();
          }}
        >
          <div className="efgrid">
            <label className="ef">
              Den
              <input type="date" value={day} min={todayISO()} onChange={(e) => setDay(e.target.value)} required />
            </label>
            <label className="ef wide2">
              Poznámka
              <input value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} placeholder="např. plný dvůr, závora v opravě" />
            </label>
          </div>
          <div className="efactions">
            {CLOSURE_FIELDS.map((f) => (
              <label key={f} className="chk">
                <input type="checkbox" checked={gates[f]} onChange={(e) => setGates({ ...gates, [f]: e.target.checked })} /> {GATE_LABEL[f]}
              </label>
            ))}
            <button type="submit" className="btn primary" disabled={!dayOk || !anyGate || add.isPending}>
              {add.isPending ? 'Ukládám…' : 'Zavřít den'}
            </button>
            {message && (
              <span className="arrive-error" role="alert">
                {message}
              </span>
            )}
          </div>
        </form>
      ) : (
        <div className="efactions">
          <span className="empty">Uzavírky nastavuje majitel.</span>
        </div>
      )}
    </div>
  );
}
